import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';

import PageContainer from '../components/PageContainer';
import OrganizeGeneralClusterPreview from '../components/OrganizeGeneralClusterPreview';
import { useAppBootstrap } from '../context/AppBootstrapContext';
import { visuallyHiddenSx } from '../utils/visuallyHidden';
import {
  ClusterApplyResponse,
  ClusterGenerationJob,
  ClusterPreviewResponse,
  ClusterSuggestionListItem,
  LightweightSubdomainListItem,
  PromotionEligibilityResponse,
  applyCluster,
  approveCluster,
  createPromotionSuggestion,
  deferCluster,
  fetchClusterJob,
  fetchClusterJobContext,
  fetchClusterPreview,
  fetchClusterSuggestions,
  fetchLightweightSubdomains,
  fetchPromotionEligibility,
  getOrganizeGeneralActiveJobId,
  getOrganizeGeneralErrorClass,
  getPromotionErrorCode,
  parseOrganizeGeneralApiError,
  rejectCluster,
  startClusterGenerationJob,
  undoCluster,
} from '../api/organizeGeneral';
import { useNavigate } from 'react-router-dom';
import {
  formatClusterJobStatusMessage,
  isActiveClusterJobStatus,
  resolveClusterJobContext,
} from '../utils/clusterJobStageDisplay';

const CLUSTER_JOB_POLL_INTERVAL_MS = 2000;

function buildManualIdempotencyKey(): string {
  return `organize-general-manual-${Date.now()}`;
}

const OrganizeGeneralPage: React.FC = () => {
  const navigate = useNavigate();
  const { bootstrap, loading: bootstrapLoading } = useAppBootstrap();
  const organizeGeneralEnabled = bootstrap?.capabilities?.organize_general_enabled ?? false;
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedClusterId = searchParams.get('cluster_id');

  const [loading, setLoading] = useState(true);
  const [clusters, setClusters] = useState<ClusterSuggestionListItem[]>([]);
  const [featureDisabled, setFeatureDisabled] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [job, setJob] = useState<ClusterGenerationJob | null>(null);
  const [jobError, setJobError] = useState<string | null>(null);
  const [pollError, setPollError] = useState<string | null>(null);
  const [scanLoading, setScanLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState<ClusterPreviewResponse | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewNotFound, setPreviewNotFound] = useState(false);
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [ignoring, setIgnoring] = useState(false);
  const [decisionError, setDecisionError] = useState<string | null>(null);
  const [completionToast, setCompletionToast] = useState<{
    severity: 'success' | 'warning' | 'error';
    message: string;
    clusterId?: string;
    undoExpiresAt?: string | null;
    appliedCount?: number;
  } | null>(null);
  const [undoLoading, setUndoLoading] = useState(false);

  const [subdomains, setSubdomains] = useState<LightweightSubdomainListItem[]>([]);
  const [subdomainEligibility, setSubdomainEligibility] = useState<
    Record<string, PromotionEligibilityResponse>
  >({});
  const [subdomainEligibilityErrors, setSubdomainEligibilityErrors] = useState<
    Record<string, string>
  >({});
  const [subdomainsLoading, setSubdomainsLoading] = useState(false);
  const [subdomainsError, setSubdomainsError] = useState<string | null>(null);
  const [promoteLoadingId, setPromoteLoadingId] = useState<string | null>(null);
  const [promoteError, setPromoteError] = useState<string | null>(null);

  const monitoredJobIdRef = useRef<string | null>(null);
  const pollFetchInFlightRef = useRef(false);
  const isMountedRef = useRef(true);
  const previousJobStatusRef = useRef<string | null>(null);
  const previewRequestIdRef = useRef(0);

  const clearSelectedCluster = useCallback(() => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete('cluster_id');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const handleClosePreview = useCallback(() => {
    previewRequestIdRef.current += 1;
    clearSelectedCluster();
    setPreview(null);
    setPreviewError(null);
    setPreviewNotFound(false);
    setPreviewLoading(false);
    setApproveError(null);
    setApproving(false);
    setRejecting(false);
    setIgnoring(false);
    setDecisionError(null);
  }, [clearSelectedCluster]);

  const markClusterDecisionUnavailable = useCallback(() => {
    previewRequestIdRef.current += 1;
    clearSelectedCluster();
    setPreview(null);
    setPreviewNotFound(true);
    setApproveError(null);
    setDecisionError(null);
  }, [clearSelectedCluster]);

  const resolveApplyToastSeverity = useCallback((result: ClusterApplyResponse) => {
    if (result.failed_count > 0 || result.applied_count === 0) {
      return 'warning' as const;
    }
    return 'success' as const;
  }, []);

  const buildApplyToastMessage = useCallback((result: ClusterApplyResponse) => {
    const label = result.lightweight_subdomain_label || 'subdomain';
    const parts = [
      `Assigned ${result.applied_count} item${result.applied_count === 1 ? '' : 's'} to ${label}.`,
    ];
    if (result.skipped_count > 0) {
      parts.push(`${result.skipped_count} skipped.`);
    }
    if (result.failed_count > 0) {
      parts.push(`${result.failed_count} failed.`);
    }
    return parts.join(' ');
  }, []);

  const selectCluster = useCallback((clusterId: string) => {
    setPreviewNotFound(false);
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set('cluster_id', clusterId);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const refreshClusters = useCallback(async () => {
    try {
      const response = await fetchClusterSuggestions();
      if (!isMountedRef.current) return;
      setClusters(response.clusters);
      setLoadError(null);
      setFeatureDisabled(false);
    } catch (err: unknown) {
      if (!isMountedRef.current) return;
      const errorClass = getOrganizeGeneralErrorClass(err);
      if (errorClass === 'organize_general_disabled') {
        setFeatureDisabled(true);
        setClusters([]);
        setLoadError(null);
        return;
      }
      setLoadError(parseOrganizeGeneralApiError(err, 'Failed to load cluster suggestions.'));
    }
  }, []);

  const refreshSubdomains = useCallback(async () => {
    setSubdomainsLoading(true);
    setSubdomainsError(null);
    try {
      const response = await fetchLightweightSubdomains();
      if (!isMountedRef.current) return;
      setSubdomains(response.subdomains);
      const eligibilityEntries = await Promise.all(
        response.subdomains.map(async (subdomain) => {
          try {
            const eligibility = await fetchPromotionEligibility(subdomain.id);
            return { id: subdomain.id, eligibility, error: null as string | null };
          } catch (err: unknown) {
            return {
              id: subdomain.id,
              eligibility: null,
              error: parseOrganizeGeneralApiError(
                err,
                'Could not check promotion eligibility for this grouping.',
              ),
            };
          }
        }),
      );
      if (!isMountedRef.current) return;
      const nextEligibility: Record<string, PromotionEligibilityResponse> = {};
      const nextEligibilityErrors: Record<string, string> = {};
      for (const entry of eligibilityEntries) {
        if (entry.eligibility) {
          nextEligibility[entry.id] = entry.eligibility;
        }
        if (entry.error) {
          nextEligibilityErrors[entry.id] = entry.error;
        }
      }
      setSubdomainEligibility(nextEligibility);
      setSubdomainEligibilityErrors(nextEligibilityErrors);
    } catch (err: unknown) {
      if (!isMountedRef.current) return;
      const errorClass = getOrganizeGeneralErrorClass(err);
      if (errorClass === 'organize_general_disabled') {
        setFeatureDisabled(true);
        setSubdomains([]);
        return;
      }
      setSubdomainsError(parseOrganizeGeneralApiError(err, 'Failed to load your subdomains.'));
    } finally {
      if (isMountedRef.current) {
        setSubdomainsLoading(false);
      }
    }
  }, []);

  const handlePromoteSubdomain = useCallback(async (subdomainId: string) => {
    setPromoteLoadingId(subdomainId);
    setPromoteError(null);
    try {
      const eligibility = subdomainEligibility[subdomainId];
      if (eligibility?.existing_suggestion_id) {
        navigate(`/app/modes/suggestions/${eligibility.existing_suggestion_id}`);
        return;
      }
      const result = await createPromotionSuggestion(subdomainId);
      if (!isMountedRef.current) return;
      navigate(`/app/modes/suggestions/${result.id}`);
    } catch (err: unknown) {
      if (!isMountedRef.current) return;
      const code = getPromotionErrorCode(err);
      if (code === 'promotion_suggestion_exists') {
        const detail = (err as { response?: { data?: { detail?: { existing_suggestion_id?: string } } } })
          ?.response?.data?.detail;
        if (detail?.existing_suggestion_id) {
          navigate(`/app/modes/suggestions/${detail.existing_suggestion_id}`);
          return;
        }
      }
      setPromoteError(parseOrganizeGeneralApiError(err, 'Failed to start promotion preview.'));
    } finally {
      if (isMountedRef.current) {
        setPromoteLoadingId(null);
      }
    }
  }, [navigate, subdomainEligibility]);

  const handleClusterDecisionError = useCallback(async (
    err: unknown,
    fallbackMessage: string,
  ) => {
    const errorClass = getOrganizeGeneralErrorClass(err);
    if (errorClass === 'organize_general_disabled') {
      setFeatureDisabled(true);
      return;
    }
    const axiosErr = err as { response?: { status?: number } };
    const isUnavailable =
      axiosErr.response?.status === 404
      || errorClass === 'invalid_cluster_status'
      || errorClass === 'cluster_not_found';
    if (isUnavailable) {
      await refreshClusters();
      markClusterDecisionUnavailable();
      setStatusMessage(
        parseOrganizeGeneralApiError(err, 'This suggestion is no longer available.'),
      );
      return;
    }
    const message = parseOrganizeGeneralApiError(err, fallbackMessage);
    setDecisionError(message);
    setStatusMessage(message);
  }, [markClusterDecisionUnavailable, refreshClusters]);

  const handleRejectCluster = useCallback(async () => {
    if (!selectedClusterId) return;
    setRejecting(true);
    setApproveError(null);
    setDecisionError(null);
    try {
      setStatusMessage('Rejecting suggestion…');
      await rejectCluster(selectedClusterId);
      if (!isMountedRef.current) return;
      await refreshClusters();
      handleClosePreview();
      setStatusMessage('Suggestion rejected.');
    } catch (err: unknown) {
      if (!isMountedRef.current) return;
      await handleClusterDecisionError(err, 'Failed to reject cluster.');
    } finally {
      if (isMountedRef.current) {
        setRejecting(false);
      }
    }
  }, [handleClosePreview, handleClusterDecisionError, refreshClusters, selectedClusterId]);

  const handleIgnoreCluster = useCallback(async () => {
    if (!selectedClusterId) return;
    setIgnoring(true);
    setApproveError(null);
    setDecisionError(null);
    try {
      setStatusMessage('Dismissing suggestion…');
      await deferCluster(selectedClusterId);
      if (!isMountedRef.current) return;
      await refreshClusters();
      handleClosePreview();
      setStatusMessage('Suggestion dismissed.');
    } catch (err: unknown) {
      if (!isMountedRef.current) return;
      await handleClusterDecisionError(err, 'Failed to dismiss cluster.');
    } finally {
      if (isMountedRef.current) {
        setIgnoring(false);
      }
    }
  }, [handleClosePreview, handleClusterDecisionError, refreshClusters, selectedClusterId]);

  const handleApproveCluster = useCallback(async () => {
    if (!selectedClusterId) return;
    setApproving(true);
    setApproveError(null);
    setDecisionError(null);
    let phase: 'approve' | 'apply' = 'approve';
    try {
      setStatusMessage('Approving cluster…');
      await approveCluster(selectedClusterId);
      if (!isMountedRef.current) return;

      phase = 'apply';
      setStatusMessage('Applying subdomain assignment…');
      const applyResult = await applyCluster(selectedClusterId);
      if (!isMountedRef.current) return;
      await refreshClusters();
      handleClosePreview();
      setCompletionToast({
        severity: resolveApplyToastSeverity(applyResult),
        message: buildApplyToastMessage(applyResult),
        clusterId: applyResult.cluster_id,
        undoExpiresAt: applyResult.undo_expires_at,
        appliedCount: applyResult.applied_count,
      });
      setStatusMessage(
        `Subdomain apply complete. ${applyResult.applied_count} applied, ${applyResult.skipped_count} skipped.`,
      );
    } catch (err: unknown) {
      if (!isMountedRef.current) return;
      const errorClass = getOrganizeGeneralErrorClass(err);
      if (errorClass === 'organize_general_disabled') {
        setFeatureDisabled(true);
        return;
      }
      if (phase === 'apply') {
        await refreshClusters();
      }
      const fallback = phase === 'apply'
        ? 'Failed to apply subdomain assignment.'
        : 'Failed to approve cluster.';
      const message = parseOrganizeGeneralApiError(err, fallback);
      setApproveError(message);
      setStatusMessage(message);
    } finally {
      if (isMountedRef.current) {
        setApproving(false);
      }
    }
  }, [
    buildApplyToastMessage,
    handleClosePreview,
    refreshClusters,
    resolveApplyToastSeverity,
    selectedClusterId,
  ]);

  const handleUndoCluster = useCallback(async () => {
    if (!completionToast?.clusterId) return;
    setUndoLoading(true);
    setStatusMessage('Undoing subdomain assignment…');
    try {
      const result = await undoCluster(completionToast.clusterId);
      if (!isMountedRef.current) return;
      setCompletionToast({
        severity: 'success',
        message: `Restored ${result.restored_count} item${result.restored_count === 1 ? '' : 's'}.`,
      });
      setStatusMessage(`Undo complete. ${result.restored_count} restored.`);
      await refreshClusters();
    } catch (err: unknown) {
      if (!isMountedRef.current) return;
      setCompletionToast({
        severity: 'error',
        message: parseOrganizeGeneralApiError(err, 'Failed to undo subdomain assignment.'),
      });
      setStatusMessage(parseOrganizeGeneralApiError(err, 'Failed to undo subdomain assignment.'));
    } finally {
      if (isMountedRef.current) {
        setUndoLoading(false);
      }
    }
  }, [completionToast?.clusterId, refreshClusters]);

  const loadPreview = useCallback(async (clusterId: string) => {
    const requestId = previewRequestIdRef.current + 1;
    previewRequestIdRef.current = requestId;
    setPreviewLoading(true);
    setPreview(null);
    setPreviewError(null);
    setPreviewNotFound(false);
    try {
      const response = await fetchClusterPreview(clusterId);
      if (!isMountedRef.current || previewRequestIdRef.current !== requestId) return;
      setPreview(response);
    } catch (err: unknown) {
      if (!isMountedRef.current || previewRequestIdRef.current !== requestId) return;
      const axiosErr = err as { response?: { status?: number } };
      if (axiosErr.response?.status === 404) {
        setPreviewNotFound(true);
        clearSelectedCluster();
        return;
      }
      const errorClass = getOrganizeGeneralErrorClass(err);
      if (errorClass === 'organize_general_disabled') {
        setFeatureDisabled(true);
        clearSelectedCluster();
        return;
      }
      setPreviewError(parseOrganizeGeneralApiError(err, 'Failed to load cluster preview.'));
    } finally {
      if (isMountedRef.current && previewRequestIdRef.current === requestId) {
        setPreviewLoading(false);
      }
    }
  }, [clearSelectedCluster]);

  const applyJob = useCallback((nextJob: ClusterGenerationJob | null) => {
    const previousStatus = previousJobStatusRef.current;
    const nextStatus = nextJob?.status ?? null;
    setJob(nextJob);
    if (nextJob?.failure_reason && nextJob.status === 'failed') {
      setJobError(nextJob.failure_reason);
    } else if (nextJob?.status !== 'failed') {
      setJobError(null);
    }
    if (nextStatus === 'completed' && previousStatus !== 'completed') {
      setStatusMessage('Scan complete. Updated suggestions are ready.');
      void refreshClusters();
    }
    previousJobStatusRef.current = nextStatus;
  }, [refreshClusters]);

  const pollJob = useCallback(async (jobId: string) => {
    if (pollFetchInFlightRef.current) return;
    pollFetchInFlightRef.current = true;
    try {
      const nextJob = await fetchClusterJob(jobId);
      if (!isMountedRef.current || monitoredJobIdRef.current !== jobId) return;
      setPollError(null);
      applyJob(nextJob);
    } catch (err: unknown) {
      if (!isMountedRef.current) return;
      setPollError(parseOrganizeGeneralApiError(err, 'Failed to refresh scan status.'));
    } finally {
      pollFetchInFlightRef.current = false;
    }
  }, [applyJob]);

  const hydrate = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setFeatureDisabled(false);
    try {
      if (bootstrapLoading) {
        return;
      }
      if (!organizeGeneralEnabled) {
        setFeatureDisabled(true);
        setClusters([]);
        setJob(null);
        return;
      }

      const [jobContextResult, listResult] = await Promise.all([
        fetchClusterJobContext()
          .then((value) => ({ ok: true as const, value }))
          .catch((err: unknown) => ({ ok: false as const, err })),
        fetchClusterSuggestions()
          .then((value) => ({ ok: true as const, value }))
          .catch((err: unknown) => ({ ok: false as const, err })),
      ]);
      if (!isMountedRef.current) return;

      void refreshSubdomains();

      if (listResult.ok) {
        setClusters(listResult.value.clusters);
        setLoadError(null);
      } else {
        const listErrorClass = getOrganizeGeneralErrorClass(listResult.err);
        if (listErrorClass === 'organize_general_disabled') {
          setFeatureDisabled(true);
          setClusters([]);
        } else {
          setLoadError(
            parseOrganizeGeneralApiError(listResult.err, 'Failed to load cluster suggestions.'),
          );
        }
      }

      if (jobContextResult.ok) {
        const resolvedJob = resolveClusterJobContext(
          jobContextResult.value.active_job,
          jobContextResult.value.last_terminal_job,
        ) as ClusterGenerationJob | null;
        applyJob(resolvedJob);
        monitoredJobIdRef.current =
          jobContextResult.value.active_job?.id ?? resolvedJob?.id ?? null;
      } else {
        const jobErrorClass = getOrganizeGeneralErrorClass(jobContextResult.err);
        if (jobErrorClass === 'organize_general_disabled') {
          setFeatureDisabled(true);
          setClusters([]);
        } else if (!listResult.ok) {
          setLoadError(
            parseOrganizeGeneralApiError(jobContextResult.err, 'Failed to load Organize General.'),
          );
        }
      }
    } catch (err: unknown) {
      if (!isMountedRef.current) return;
      setLoadError(parseOrganizeGeneralApiError(err, 'Failed to load Organize General.'));
    } finally {
      if (isMountedRef.current && !bootstrapLoading) {
        setLoading(false);
      }
    }
  }, [applyJob, bootstrapLoading, organizeGeneralEnabled, refreshSubdomains]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    void hydrate();
  }, [hydrate]);

  useEffect(() => {
    if (!selectedClusterId) {
      if (!previewNotFound) {
        setPreview(null);
        setPreviewError(null);
        setPreviewLoading(false);
      }
      return undefined;
    }
    if (previewNotFound) {
      return undefined;
    }
    if (featureDisabled || !organizeGeneralEnabled) {
      previewRequestIdRef.current += 1;
      clearSelectedCluster();
      setPreview(null);
      setPreviewError(null);
      setPreviewNotFound(false);
      setPreviewLoading(false);
      return undefined;
    }
    void loadPreview(selectedClusterId);
    return undefined;
  }, [clearSelectedCluster, featureDisabled, loadPreview, organizeGeneralEnabled, previewNotFound, selectedClusterId]);

  useEffect(() => {
    const jobId = job?.id ?? null;
    monitoredJobIdRef.current = jobId;
    if (!jobId || !isActiveClusterJobStatus(job?.status)) {
      return undefined;
    }
    const intervalId = window.setInterval(() => {
      void pollJob(jobId);
    }, CLUSTER_JOB_POLL_INTERVAL_MS);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [job?.id, job?.status, pollJob]);

  useEffect(() => {
    if (!selectedClusterId && !preview && !previewNotFound) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleClosePreview();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleClosePreview, preview, previewNotFound, selectedClusterId]);

  const handleStartScan = useCallback(async () => {
    setScanLoading(true);
    setJobError(null);
    setPollError(null);
    setStatusMessage(null);
    try {
      const response = await startClusterGenerationJob(buildManualIdempotencyKey());
      monitoredJobIdRef.current = response.job_id;
      try {
        const nextJob = await fetchClusterJob(response.job_id);
        if (!isMountedRef.current) return;
        applyJob(nextJob);
      } catch (fetchErr: unknown) {
        if (!isMountedRef.current) return;
        applyJob({
          id: response.job_id,
          status: response.status || 'queued',
          requested_at: new Date().toISOString(),
        });
        setPollError(parseOrganizeGeneralApiError(fetchErr, 'Failed to refresh scan status.'));
      }
      setStatusMessage('Scan started. We will refresh suggestions when it finishes.');
    } catch (err: unknown) {
      if (!isMountedRef.current) return;
      const errorClass = getOrganizeGeneralErrorClass(err);
      if (errorClass === 'active_cluster_generation_job') {
        const activeJobId = getOrganizeGeneralActiveJobId(err);
        if (activeJobId) {
          monitoredJobIdRef.current = activeJobId;
          try {
            const activeJob = await fetchClusterJob(activeJobId);
            if (!isMountedRef.current) return;
            applyJob(activeJob);
            setStatusMessage('A scan is already running.');
          } catch (fetchErr: unknown) {
            if (!isMountedRef.current) return;
            applyJob({
              id: activeJobId,
              status: 'queued',
              requested_at: new Date().toISOString(),
            });
            setPollError(parseOrganizeGeneralApiError(fetchErr, 'Failed to refresh scan status.'));
          }
          return;
        }
      }
      if (errorClass === 'insufficient_general_items') {
        setJobError(
          parseOrganizeGeneralApiError(
            err,
            'You need at least three General items before we can scan for groups.',
          ),
        );
        return;
      }
      if (errorClass === 'organize_general_disabled') {
        setFeatureDisabled(true);
        return;
      }
      setJobError(parseOrganizeGeneralApiError(err, 'Failed to start scan.'));
    } finally {
      if (isMountedRef.current) {
        setScanLoading(false);
      }
    }
  }, [applyJob]);

  const showProgress = Boolean(job && isActiveClusterJobStatus(job.status));
  const showPreviewPanel = Boolean(selectedClusterId || previewLoading || preview || previewError || previewNotFound);
  const showEmptyState =
    !loading &&
    !featureDisabled &&
    !loadError &&
    clusters.length === 0 &&
    !showProgress &&
    !jobError &&
    !showPreviewPanel;
  const showFailedJob = Boolean(job && job.status === 'failed');
  const showClusterSection = !loading && !featureDisabled && (clusters.length > 0 || showPreviewPanel);
  const showSubdomainsSection = !loading && !featureDisabled && (subdomains.length > 0 || subdomainsLoading);

  return (
    <PageContainer>
      <Box className="space-y-4">
        <Box>
          <Typography variant="h4" component="h1" className="font-semibold">
            Organize General
          </Typography>
          <Typography variant="body1" color="text.secondary" className="mt-1">
            Review suggested groups in your General items. Approving assigns a lightweight subdomain while items stay in General.
          </Typography>
        </Box>

        <Box
          role="status"
          aria-live="polite"
          aria-atomic="true"
          data-testid="ORGANIZE_GENERAL_STATUS"
          sx={visuallyHiddenSx}
        >
          {statusMessage ?? (showProgress ? formatClusterJobStatusMessage(job?.progress_stage, job?.metrics) : '')}
        </Box>

        {loading ? (
          <Box className="flex justify-center py-8" data-testid="ORGANIZE_GENERAL_LOADING">
            <CircularProgress aria-label="Loading Organize General" />
          </Box>
        ) : null}

        {featureDisabled ? (
          <Alert severity="info" data-testid="ORGANIZE_GENERAL_DISABLED">
            Organize General is not available in this environment yet.
          </Alert>
        ) : null}

        {loadError ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => void hydrate()}>
                Retry
              </Button>
            }
            data-testid="ORGANIZE_GENERAL_LOAD_ERROR"
          >
            {loadError}
          </Alert>
        ) : null}

        {showSubdomainsSection ? (
          <Box data-testid="ORGANIZE_GENERAL_SUBDOMAINS_SECTION">
            <Typography variant="h6" component="h2" className="mb-3">
              Your subdomains
            </Typography>
            {subdomainsLoading ? (
              <Box className="flex justify-center py-4">
                <CircularProgress size={24} aria-label="Loading subdomains" />
              </Box>
            ) : null}
            {subdomainsError ? (
              <Alert
                severity="warning"
                action={
                  <Button color="inherit" size="small" onClick={() => void refreshSubdomains()}>
                    Retry
                  </Button>
                }
              >
                {subdomainsError}
              </Alert>
            ) : null}
            {promoteError ? (
              <Alert severity="error" className="mb-3">
                {promoteError}
              </Alert>
            ) : null}
            {subdomains.length > 0 ? (
              <List data-testid="ORGANIZE_GENERAL_SUBDOMAIN_LIST">
                {subdomains.map((subdomain) => {
                  const eligibility = subdomainEligibility[subdomain.id];
                  const eligibilityError = subdomainEligibilityErrors[subdomain.id];
                  const eligible = eligibility?.eligible === true;
                  const ineligibleReason =
                    eligibilityError ??
                    eligibility?.reasons?.[0] ??
                    'Promotion is not available yet.';
                  const promoteButton = (
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={!eligible || promoteLoadingId === subdomain.id || Boolean(eligibilityError)}
                      onClick={() => void handlePromoteSubdomain(subdomain.id)}
                      data-testid={`ORGANIZE_GENERAL_PROMOTE_${subdomain.id}`}
                    >
                      {promoteLoadingId === subdomain.id ? 'Starting…' : 'Promote to full mode'}
                    </Button>
                  );
                  return (
                    <ListItemButton
                      key={subdomain.id}
                      divider
                      component="div"
                      data-testid={`ORGANIZE_GENERAL_SUBDOMAIN_ROW_${subdomain.id}`}
                    >
                      <ListItemText
                        primary={subdomain.display_label}
                        secondary={`${subdomain.work_item_count} items in General`}
                      />
                      {eligible && !eligibilityError ? (
                        promoteButton
                      ) : (
                        <Tooltip title={ineligibleReason}>
                          <span>{promoteButton}</span>
                        </Tooltip>
                      )}
                    </ListItemButton>
                  );
                })}
              </List>
            ) : null}
          </Box>
        ) : null}

        {showProgress ? (
          <Alert severity="info" data-testid="ORGANIZE_GENERAL_JOB_PROGRESS">
            <Typography variant="body2" className="font-medium">
              {formatClusterJobStatusMessage(job?.progress_stage, job?.metrics)}
            </Typography>
            <LinearProgress className="mt-2" />
          </Alert>
        ) : null}

        {pollError ? (
          <Alert severity="warning" data-testid="ORGANIZE_GENERAL_POLL_ERROR">
            {pollError}
          </Alert>
        ) : null}

        {(showFailedJob || jobError) ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => void handleStartScan()} disabled={scanLoading}>
                Retry scan
              </Button>
            }
            data-testid="ORGANIZE_GENERAL_JOB_FAILED"
          >
            {jobError ?? job?.failure_reason ?? 'The last scan did not finish.'}
          </Alert>
        ) : null}

        {showEmptyState ? (
          <Alert
            severity="info"
            action={
              <Button color="inherit" size="small" onClick={() => void handleStartScan()} disabled={scanLoading}>
                Scan for groups
              </Button>
            }
            data-testid="ORGANIZE_GENERAL_EMPTY"
          >
            No group suggestions yet. Scan your General items to find reviewable clusters.
          </Alert>
        ) : null}

        {showClusterSection ? (
          <Box
            className={
              clusters.length > 0 && showPreviewPanel
                ? 'grid gap-4 md:grid-cols-2 md:items-start'
                : 'grid gap-4'
            }
          >
            {clusters.length > 0 ? (
              <Box>
                <Box className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <Typography variant="h6" component="h2">
                    Suggested groups ({clusters.length})
                  </Typography>
                  {!showProgress ? (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => void handleStartScan()}
                      disabled={scanLoading}
                      data-testid="ORGANIZE_GENERAL_SCAN_BUTTON"
                    >
                      Scan for groups
                    </Button>
                  ) : null}
                </Box>
                <List data-testid="ORGANIZE_GENERAL_CLUSTER_LIST">
                  {clusters.map((cluster) => {
                    const isSelected = selectedClusterId === cluster.id;
                    return (
                      <ListItemButton
                        key={cluster.id}
                        divider
                        selected={isSelected}
                        aria-selected={isSelected}
                        tabIndex={0}
                        data-cluster-id={cluster.id}
                        data-testid={`ORGANIZE_GENERAL_CLUSTER_ROW_${cluster.id}`}
                        onClick={() => selectCluster(cluster.id)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            selectCluster(cluster.id);
                          }
                        }}
                      >
                        <ListItemText
                          primary={cluster.suggested_label}
                          secondary={`${cluster.item_count} items · ${cluster.confidence_cue}`}
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              </Box>
            ) : null}

            {showPreviewPanel ? (
              <Box data-testid="ORGANIZE_GENERAL_PREVIEW_PANEL">
                <OrganizeGeneralClusterPreview
                  loading={previewLoading}
                  preview={preview}
                  error={previewError}
                  notFound={previewNotFound}
                  approving={approving}
                  approveError={approveError}
                  rejecting={rejecting}
                  ignoring={ignoring}
                  decisionError={decisionError}
                  onClose={handleClosePreview}
                  onRetry={() => {
                    if (selectedClusterId) {
                      void loadPreview(selectedClusterId);
                    }
                  }}
                  onApprove={() => {
                    void handleApproveCluster();
                  }}
                  onReject={() => {
                    void handleRejectCluster();
                  }}
                  onIgnore={() => {
                    void handleIgnoreCluster();
                  }}
                />
              </Box>
            ) : null}
          </Box>
        ) : null}

        <Snackbar
          open={Boolean(completionToast)}
          autoHideDuration={8000}
          onClose={() => setCompletionToast(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setCompletionToast(null)}
            severity={completionToast?.severity ?? 'info'}
            variant="filled"
            data-testid="ORGANIZE_GENERAL_COMPLETION_TOAST"
            action={
              completionToast?.clusterId
              && completionToast.undoExpiresAt
              && (completionToast.appliedCount ?? 0) > 0 ? (
                <Button
                  color="inherit"
                  size="small"
                  disabled={undoLoading}
                  onClick={() => void handleUndoCluster()}
                  data-testid="ORGANIZE_GENERAL_UNDO_BUTTON"
                >
                  Undo
                </Button>
              ) : undefined
            }
          >
            {completionToast?.message}
          </Alert>
        </Snackbar>
      </Box>
    </PageContainer>
  );
};

export default OrganizeGeneralPage;
